import { useEffect, useState } from "react";
import { Briefcase, Handshake, MessageCircle, PenLine, RefreshCw, Smile } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Btn, Modal, useIsDesktop } from "@/components/kit";
import { useAuth } from "@/context/auth";

export const WA_TEMPLATES = [
  { key: "first", title: "First Connection", icon: Handshake, text: "Hi [Name], it was great connecting with you. I’m glad we exchanged contact details. Looking forward to staying in touch!" },
  { key: "nice", title: "Nice Meeting You", icon: Smile, text: "Hello [Name], it was a pleasure meeting you. I enjoyed our conversation and hope we can stay connected." },
  { key: "followup", title: "Business Follow-up", icon: RefreshCw, text: "Hi [Name], following up on our recent conversation. It would be great to discuss this further. Please let me know a convenient time." },
  { key: "intro", title: "Business Introduction", icon: Briefcase, text: "Hi [Name], this is [Your Name]. We recently connected, and I wanted to reach out and introduce myself. Looking forward to connecting with you!" },
  { key: "custom", title: "Custom Message", icon: PenLine, text: "" },
];

const nice = (s) => {
  const t = (s || "").trim().replace(/\s+/g, " ");
  return t && t === t.toUpperCase() && /[A-Z]/.test(t) ? t.toLowerCase().replace(/\b\p{L}/gu, (m) => m.toUpperCase()) : t;
};

export function personalize(text, name, yourName) {
  let s = text;
  if (yourName) s = s.replace(/\[Your Name\]/g, yourName);
  else s = s.replace(/,?\s*this is \[Your Name\]\./g, ".").replace(/\s*\[Your Name\]/g, "");
  s = name ? s.replace(/\[Name\]/g, name) : s.replace(/\s*\[Name\]/g, "");
  return s.replace(/\s+([,.!?])/g, "$1").replace(/([,.!?])\1+/g, "$1").replace(/ {2,}/g, " ").trim();
}

export function waNumber(raw) {
  let d = (raw || "").replace(/[^\d+]/g, "");
  const plus = d.startsWith("+");
  d = d.replace(/\D/g, "");
  if (d.startsWith("00")) return d.slice(2);
  if (plus) return d;
  if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`;
  if (d.length === 10) return `91${d}`;
  return d;
}

export const waLink = (num, msg) => `https://wa.me/${num}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;

function Body({ contact, onDone }) {
  const { user } = useAuth();
  const name = nice(contact.name && contact.name !== contact.companyName ? contact.name : contact.name || "");
  const yourName = nice(user?.name || "");
  const fill = (t) => personalize(t.text, name, yourName);
  const [sel, setSel] = useState("first");
  const [msg, setMsg] = useState(() => fill(WA_TEMPLATES[0]));
  const raw = contact.whatsapp || contact.phone || contact.alternatePhone;
  const num = waNumber(raw);
  const pick = (t) => { setSel(t.key); setMsg(fill(t)); };
  const go = () => { window.open(waLink(num, msg.trim()), "_blank", "noopener"); onDone(); };

  return (
    <div className="flex flex-col gap-4 min-w-0 w-full" data-testid="wa-template-sheet">
      <p className="text-[13px] text-[#64748B] -mt-1 truncate" data-testid="wa-recipient">To <b className="text-[#0F172A]">{name || contact.companyName || "contact"}</b> · +{num}</p>
      <div className="grid grid-cols-1 gap-2 max-h-[38vh] overflow-y-auto overflow-x-hidden pr-1 min-w-0">
        {WA_TEMPLATES.map((t) => {
          const on = sel === t.key;
          return (
            <button key={t.key} onClick={() => pick(t)} data-testid={`wa-template-${t.key}`}
              className={`w-full min-w-0 flex items-start gap-3 p-3 rounded-[14px] border text-left transition-colors ${on ? "border-[#0000EC] bg-[#F5F5FF]" : "border-[#E2E8F0] hover:bg-[#F8FAFC]"}`}>
              <span className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 ${on ? "bg-[#0000EC] text-white" : "bg-[#E5E5FD] text-[#0000EC]"}`}><t.icon className="w-4 h-4" /></span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-bold truncate">{t.title}</span>
                <span className="block truncate text-[12px] text-[#64748B]">{t.text ? fill(t) : "Write your own message"}</span>
              </span>
            </button>
          );
        })}
      </div>
      <label className="block min-w-0">
        <span className="t-label flex justify-between">Message<span className="font-medium text-[12px] text-[#94A3B8]">{msg.length} chars</span></span>
        <textarea className="t-input min-h-[110px] text-[15px] py-3" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type your message…" data-testid="wa-message" />
      </label>
      <Btn onClick={go} disabled={!num || !msg.trim()} className="w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white shadow-none" variant="" testid="wa-continue"><MessageCircle className="w-5 h-5" />Continue to WhatsApp</Btn>
      <p className="text-[12px] text-center text-[#94A3B8]">WhatsApp opens with this message ready — review it and tap send there.</p>
    </div>
  );
}

export default function WhatsAppSheet({ open, onClose, contact }) {
  const isDesktop = useIsDesktop();
  const [k, setK] = useState(0);
  useEffect(() => { if (open) setK((x) => x + 1); }, [open]);
  if (!contact) return null;
  const body = <Body key={k} contact={contact} onDone={onClose} />;
  if (isDesktop) return <Modal open={open} onClose={onClose} title="Send a WhatsApp message" testid="wa-modal">{body}</Modal>;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-[24px] max-h-[92vh] overflow-y-auto px-5 pb-6" data-testid="wa-bottom-sheet">
        <div className="mx-auto -mt-2 mb-3 w-10 h-1.5 rounded-full bg-[#E2E8F0]" />
        <SheetTitle className="text-[19px] font-bold">Send a WhatsApp message</SheetTitle>
        <SheetDescription className="sr-only">Choose a template</SheetDescription>
        <div className="mt-3">{body}</div>
      </SheetContent>
    </Sheet>
  );
}
